import classNames from 'classnames/bind';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Avatar from '~/components/Avatar';

import config from '~/config';

import ImagePreview from '../Gallery/ImagePreview';
import { StreamPreview } from '../Stream/List/StreamPreview';
import styles from './LinkPreview.scss';

const cx = classNames.bind(styles);

const piczelLinkPattern =
  /https?:\/\/(?:www\.)?piczel.tv\/((gallery\/image)|(watch)|(gallery))\/([a-zA-Z0-9]*)/gm;
const galleryPattern = /\/gallery\/[a-zA-Z0-9]+\/?$/;
const imagePattern = /\/gallery\/image\/[a-zA-Z0-9]+$/;

function usePiczelLinks(text) {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    const matches = text.match(piczelLinkPattern) || [];

    // filter out folder links (links that match folderPattern and not imagePattern)
    const filteredMatches = matches.filter((link) => {
      if (galleryPattern.test(link)) {
        return imagePattern.test(link);
      }

      return true; // keep all other links
    });

    setLinks(filteredMatches);
  }, [text]);

  return links;
}

const postGallery = '/gallery/image/';
const gallery = '/gallery/';
const watch = '/watch/';
const galleryFolder = '/gallery-folder/'; // placeholder tag for gallery/<user>/<folderId> links (no preview yet)

function getTypeLink(link) {
  if (link.includes(postGallery)) return postGallery;
  if (link.includes(gallery)) return gallery;
  if (link.includes(watch)) return watch;
  // gallery/<user>/<something-else>  → folder
  const folderMatch = link.match(galleryPattern);
  if (folderMatch) return galleryFolder;
  return null;
}

function getDetailsLink(link) {
  let route = '';

  if (getTypeLink(link) === galleryFolder) {
    // Nothing to fetch yet – resolve to null
    return Promise.resolve(null);
  }

  if (link.includes(postGallery)) {
    const id = link.split(postGallery)[1] || '';
    route = `/gallery/${id}`;
  } else if (link.includes(gallery)) {
    const id = link.split(gallery)[1] || '';
    route = `/users/${id}/gallery?limit=4`;
  } else if (link.includes(watch)) {
    const id = link.split(watch)[1] || '';
    route = `/streams/${id}`;
  }
  return fetch(`${config.api}${route}`).then((r) => r.json());
}

function UrlStreamPreview(props) {
  const { username, adult, onClick } = props;
  const [showSpoiler, setShowSpoiler] = useState(!adult);
  const clickShowSpoiler = useCallback(
    () => setShowSpoiler(true),
    [setShowSpoiler]
  );

  return (
    <div>
      <div>
        <Avatar
          className={styles.StreamPreview__Avatar}
          username={username}
          user={username}
        />
        <span>{username}</span>
      </div>

      <div style={{ position: 'relative' }}>
        <div onClick={(e) => onClick(null, e)}>
          <StreamPreview stream={props} />
        </div>

        {!showSpoiler && (
          <div
            onClick={clickShowSpoiler}
            className={styles.StreamPreview__Spoiler}
          />
        )}
      </div>
    </div>
  );
}

function UrlImagePreview(props) {
  const { nsfw, onClick } = props;
  const [showSpoiler, setShowSpoiler] = useState(!nsfw);
  const clickShowSpoiler = useCallback(
    () => setShowSpoiler(true),
    [setShowSpoiler]
  );

  return (
    <div>
      <ImagePreview {...props} onClick={onClick} />
      {!showSpoiler && (
        <div
          onClick={clickShowSpoiler}
          className={styles.GalleryPostPreview__Spoiler}
        />
      )}
    </div>
  );
}

function GalleryPreview({ images }) {
  return images.map((img) => (
    <div
      key={img.id}
      className={styles.GalleryPreview__Thumb}
      style={{ backgroundImage: `url(${img.image.thumb.url})` }}
    />
  ));
}

function MesssageWithLinkPreview({ msg, onLoadPreview }) {
  const links = usePiczelLinks(msg.text);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Cache the link type so we don’t recompute it several times.
  const type = links.length ? getTypeLink(links[0]) : null;

  const onClick = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(links[0]);
  };

  useEffect(() => {
    setError(false); // reset for a new link
    setDetails(null);
    if (!links.length) return;
    getDetailsLink(links[0]).then((res) => {
      // Folder link → getDetailsLink() resolved to null
      if (res === null) {
        onLoadPreview?.(); // still let the parent know we finished
        return;
      }
      if (res.status === 'error' || (Array.isArray(res) && !res.length)) {
        setError(true);
      } else if (res.errors && res?.meta?.wrong_pass) {
        setDetails({ data: res.meta.wrong_pass });
        onLoadPreview();
      } else {
        setDetails(res);
        onLoadPreview();
      }
    });
  }, [links, onLoadPreview]);

  useEffect(() => {
    if (!ref.current || !msg.text) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = msg.text;
    // wrap text nodes with a span
    wrapper.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const span = document.createElement('span');
        span.textContent = node.textContent;
        node.replaceWith(span);
      }
    });

    ref.current.innerHTML = wrapper.innerHTML;
  }, [msg.text]);

  return (
    <>
      {!!links.length && !error && (
        <div
          className={cx({
            // FolderPreview goes here
            GalleryPostPreview: type === postGallery,
            GalleryPreview: type === gallery,
            StreamPreview: type === watch,
          })}
        >
          {details && type === postGallery && (
            <UrlImagePreview {...details} onClick={onClick} />
          )}

          {details?.[0]?.user && type === gallery && (
            <>
              <div onClick={(e) => onClick(null, e)}>
                <Avatar
                  className={styles.GalleryPreview__Avatar}
                  username={details[0].user.username}
                  user={details[0].user}
                />
                <span>{details[0].user.username}</span>
              </div>

              <div
                onClick={(e) => onClick(null, e)}
                className={styles.GalleryPreview__Thumbs}
              >
                <GalleryPreview images={details} />
              </div>
            </>
          )}

          {details?.data?.[0] && type === watch && (
            <UrlStreamPreview {...details.data[0]} onClick={onClick} />
          )}
        </div>
      )}

      <div ref={ref} />
    </>
  );
}

export default MesssageWithLinkPreview;
